import axios, { AxiosInstance } from 'axios';

/**
 * Invitation Response Status Enum
 * Following Single Responsibility: Clear contract for invitation status
 */
export enum InvitationStatus {
    ACCEPT = 1,
    DECLINE = 2,
    SCANNED = 4
}

/**
 * Request/Response Types (Interface Segregation)
 */
export interface AcceptDeclineInvitationRequest {
    hash: string;
    status: InvitationStatus;
    amount_of_guests: number;
}

export interface AcceptDeclineInvitationResponse {
    url: string | false;
}

/**
 * Invitation Service Interface (Dependency Inversion)
 * Depend on abstraction, not concrete implementation
 */
export interface IInvitationService {
    acceptInvitation(hash: string, guestCount?: number): Promise<AcceptDeclineInvitationResponse>;
    declineInvitation(hash: string): Promise<AcceptDeclineInvitationResponse>;
}

/**
 * Concrete Implementation of Invitation Service
 * Following Single Responsibility: Only handles invitation-related API calls
 */
export class InvitationService implements IInvitationService {
    private readonly httpClient: AxiosInstance;
    private readonly baseUrl: string;

    constructor(baseUrl: string, httpClient: AxiosInstance = axios) {
        this.baseUrl = baseUrl;
        this.httpClient = httpClient;
    }

    /**
     * Accept an invitation
     * @param hash - Invitation hash
     * @param guestCount - Number of additional guests (defaults to 0)
     */
    async acceptInvitation(
        hash: string,
        guestCount: number = 0
    ): Promise<AcceptDeclineInvitationResponse> {
        try {
            const response = await this.httpClient.post<AcceptDeclineInvitationResponse>(
                `${this.baseUrl}/events/invitation`,
                {
                    hash,
                    status: InvitationStatus.ACCEPT,
                    amount_of_guests: guestCount
                }
            );

            if (response.status === 202) {
                return response.data;
            }

            throw new Error('Unexpected response status');
        } catch (error: any) {
            // Enhanced error handling
            if (error.response?.status === 422) {
                throw new Error('Validation error: Invalid invitation data');
            } else if (error.response?.status === 500) {
                throw new Error('Cannot accept invitation (expired or already attended)');
            }
            throw new Error(error.response?.data?.message || 'Failed to accept invitation');
        }
    }

    /**
     * Decline an invitation
     * @param hash - Invitation hash
     */
    async declineInvitation(hash: string): Promise<AcceptDeclineInvitationResponse> {
        try {
            const response = await this.httpClient.post<AcceptDeclineInvitationResponse>(
                `${this.baseUrl}/events/invitation`,
                {
                    hash,
                    status: InvitationStatus.DECLINE,
                    amount_of_guests: 0
                }
            );

            if (response.status === 202) {
                return response.data;
            }

            throw new Error('Unexpected response status');
        } catch (error: any) {
            // Enhanced error handling
            if (error.response?.status === 422) {
                throw new Error('Validation error: Invalid invitation data');
            } else if (error.response?.status === 500) {
                throw new Error('Cannot decline invitation (expired or already attended)');
            }
            throw new Error(error.response?.data?.message || 'Failed to decline invitation');
        }
    }
}

/**
 * Factory function to create invitation service instance
 * Following Dependency Inversion: Makes it easy to inject different implementations
 */
export const createInvitationService = (baseUrl: string): IInvitationService => {
    return new InvitationService(baseUrl);
};
